/**
 * 独享容器：Web 终端 / 连接状态条 / 进程列表 / 实时日志（用户与后台共用，传入取票函数）。
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Dropdown, Space, Spin, Switch, Table, Typography, message } from 'antd';
import { PauseOutlined, CaretRightOutlined, ReloadOutlined, SyncOutlined } from '@ant-design/icons';
import { useTranslation } from '../../i18n';
import { TerminalConsole } from '../console';
import { isOk, errMsg } from '../../services/containerService';
import { buildWsUrl } from './filesSocket';
import { fmtBytes, arr } from './ContainerParts';

const { Text } = Typography;

/** 连接状态：连接中显示加载，断开显示「重新连接」。open 时不渲染。 */
export const SocketBar = ({ state, error, onReconnect }) => {
    const { t } = useTranslation();
    if (state === 'open') return null;
    if (state === 'connecting') return <div style={{ padding: 12 }}><Spin size="small" /> <Text type="secondary">{t('Connecting…')}</Text></div>;
    return (
        <Alert type="warning" showIcon style={{ marginBottom: 12 }} message={error || t('Disconnected')}
            action={<Button size="small" type="primary" icon={<ReloadOutlined />} onClick={onReconnect}>{t('Reconnect')}</Button>} />
    );
};

/** Web 终端：复用 TerminalConsole；每次（重）连接都取新票据。 */
export const ContainerTerminal = ({ fetchTicket, height = 480 }) => {
    const { t } = useTranslation();
    const boxRef = useRef(null);
    const [wsUrl, setWsUrl] = useState('');
    const [err, setErr] = useState('');
    const [loading, setLoading] = useState(false);
    const [closed, setClosed] = useState(false);
    const urlRef = useRef('');
    // 旧连接的关闭回调可能晚于新连接到达，只认当前 URL
    const markClosed = (u) => { if (u && urlRef.current === u) setClosed(true); };

    const open = useCallback(async () => {
        setLoading(true);
        setErr('');
        setClosed(false);
        setWsUrl('');
        urlRef.current = '';
        const r = await fetchTicket();
        setLoading(false);
        if (!isOk(r) || !r.data?.ws_url) { setErr(errMsg(r, t('Connection failed'))); return; }
        const w = boxRef.current?.clientWidth || 960;
        const cols = Math.max(40, Math.floor((w - 24) / 8.5));
        const rows = Math.max(10, Math.floor((height - 60) / 17));
        const u = buildWsUrl(r.data.ws_url, { token: r.data.token, cols, rows });
        urlRef.current = u;
        setWsUrl(u);
    }, [fetchTicket, height, t]);

    useEffect(() => { open(); }, [open]);

    return (
        <div ref={boxRef}>
            {(err || closed) && (
                <Alert type="warning" showIcon style={{ marginBottom: 8 }} message={err || t('Disconnected')}
                    action={<Button size="small" type="primary" icon={<ReloadOutlined />} loading={loading} onClick={open}>{t('Reconnect')}</Button>} />
            )}
            {loading && !wsUrl && <div style={{ padding: 24, textAlign: 'center' }}><Spin /></div>}
            {wsUrl && (
                <TerminalConsole wsUrl={wsUrl} onReconnect={open} style={{ height }}
                    onDisconnect={() => markClosed(wsUrl)} onError={() => markClosed(wsUrl)} />
            )}
        </div>
    );
};

/** 进程列表（文件通道 ps）。 */
export const ProcessList = ({ sockRef, ready }) => {
    const { t } = useTranslation();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const load = useCallback(async () => {
        const s = sockRef.current;
        if (!s) return;
        setLoading(true);
        try {
            const d = await s.call('ps');
            setRows(arr(d.processes));
        } catch (e) { message.error(e.message); }
        setLoading(false);
    }, [sockRef]);
    useEffect(() => { if (ready) load(); }, [ready, load]);
    const kill = async (pid, signal) => {
        try {
            await sockRef.current.call('kill', { pid, signal });
            message.success(t('Sent'));
            setTimeout(load, 800);
        } catch (e) { message.error(e.message); }
    };
    const cols = [
        { title: 'PID', dataIndex: 'pid', width: 80 },
        { title: 'PPID', dataIndex: 'ppid', width: 80 },
        { title: 'UID', dataIndex: 'uid', width: 70 },
        { title: t('Status'), dataIndex: 'state', width: 70 },
        { title: t('Memory'), dataIndex: 'rss_kb', width: 100, render: (v) => fmtBytes((Number(v) || 0) * 1024) },
        { title: t('Command'), dataIndex: 'cmd', render: (v) => <Text code style={{ wordBreak: 'break-all' }}>{v}</Text> },
        {
            // PID 1 是应用本身：用重启代替
            title: '', key: 'op', width: 110, render: (_, r) => (Number(r.pid) > 1 ? (
                <Dropdown.Button size="small" disabled={!ready} onClick={() => kill(Number(r.pid), 'TERM')}
                    menu={{ items: [{ key: 'KILL', label: t('Force kill'), danger: true }], onClick: () => kill(Number(r.pid), 'KILL') }}>
                    {t('End')}
                </Dropdown.Button>
            ) : null),
        },
    ];
    return (
        <div>
            <Button size="small" icon={<SyncOutlined />} loading={loading} disabled={!ready} onClick={load} style={{ marginBottom: 8 }}>{t('Refresh')}</Button>
            <Table size="small" rowKey="pid" columns={cols} dataSource={rows} pagination={false} scroll={{ y: 320 }} />
        </div>
    );
};

const MAX_LOG = 400 * 1024;

/** 实时日志：先取尾部，再 logs_follow 每 2 秒推送；支持暂停与自动滚动。 */
export const LiveLogs = ({ sockRef, ready }) => {
    const { t } = useTranslation();
    const [text, setText] = useState('');
    const [paused, setPaused] = useState(false);
    const [auto, setAuto] = useState(true);
    const preRef = useRef(null);
    const followId = useRef(null);

    const append = useCallback((s) => {
        if (!s) return;
        setText((old) => {
            const n = old + s;
            return n.length > MAX_LOG ? n.slice(n.length - MAX_LOG) : n;
        });
    }, []);

    const stop = useCallback(() => {
        const s = sockRef.current;
        if (followId.current && s) {
            s.unfollow(followId.current);
            if (s.isOpen) s.call('logs_stop').catch(() => {});
        }
        followId.current = null;
    }, [sockRef]);

    const follow = useCallback(() => {
        const s = sockRef.current;
        if (!s || followId.current) return;
        followId.current = s.follow('logs_follow', {}, (m) => {
            if (m.error) { message.error(m.error); return; }
            append(m.text ?? m.data?.text);
        });
    }, [sockRef, append]);

    useEffect(() => {
        if (!ready) { followId.current = null; return undefined; }
        let cancel = false;
        const s = sockRef.current;
        if (!s) return undefined;
        s.call('logs', { bytes: 65536 }).then((d) => {
            if (cancel) return;
            setText(String(d.text || '').slice(-MAX_LOG));
            if (!paused) follow();
        }).catch((e) => { if (!cancel) message.error(e.message); });
        return () => { cancel = true; stop(); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ready]);

    useEffect(() => {
        if (auto && preRef.current) preRef.current.scrollTop = preRef.current.scrollHeight;
    }, [text, auto]);

    const toggle = () => {
        if (paused) { follow(); setPaused(false); } else { stop(); setPaused(true); }
    };

    return (
        <div>
            <Space style={{ marginBottom: 8 }} wrap>
                <Button size="small" icon={paused ? <CaretRightOutlined /> : <PauseOutlined />} disabled={!ready} onClick={toggle}>
                    {paused ? t('Resume') : t('Suspended')}
                </Button>
                <Space size={4}><Switch size="small" checked={auto} onChange={setAuto} /><Text type="secondary">{t('Auto-scroll')}</Text></Space>
                <Button size="small" type="text" onClick={() => setText('')}>{t('Empty')}</Button>
            </Space>
            <pre ref={preRef} style={{
                margin: 0, padding: 12, height: 420, overflow: 'auto', fontSize: 12,
                fontFamily: 'Menlo, Consolas, monospace', background: '#141414', color: '#ddd', borderRadius: 6,
                whiteSpace: 'pre-wrap', wordBreak: 'break-all',
            }}>{text || t('No logs yet')}</pre>
        </div>
    );
};
